import { Alert, Button, ConfigProvider, Skeleton } from '@yimoka/antd';
import { DynamicView, type DynamicViewProps } from '@yimoka/react';
import React, { useMemo, type PropsWithChildren } from 'react';

import { useTranslation } from '@/locales/i18n';

import { RealmProfileContext } from './context';
import type { RealmProfileSource } from './context';
import { createTenantWebRealmTheme } from './profile';
import { realmHomeRuntimeComponents } from './registry';
import type { IRealmProfileSnapshot } from './types';

interface RealmProfileRendererProps {
  loading?: boolean;
  profile?: IRealmProfileSnapshot;
  source: RealmProfileSource;
  onRetry?: () => void;
}

interface RealmProfileErrorBoundaryState {
  failed: boolean;
}

interface RealmProfileErrorBoundaryProps extends PropsWithChildren {
  description: string;
  retryLabel: string;
  title: string;
  onRetry?: () => void;
}

/** RealmProfileErrorBoundary 隔离 DynamicView/React 基础设施异常，错误壳不再递归调用 Renderer。 */
class RealmProfileErrorBoundary extends React.Component<RealmProfileErrorBoundaryProps, RealmProfileErrorBoundaryState> {
  /** getDerivedStateFromError 把运行时异常收敛为中性恢复界面。 */
  static getDerivedStateFromError(): RealmProfileErrorBoundaryState {
    return { failed: true };
  }

  state: RealmProfileErrorBoundaryState = { failed: false };

  /** render 仅在安全边界外显示无品牌错误壳。 */
  render() {
    if (this.state.failed) {
      return (
        <main style={{ margin: '15vh auto', maxWidth: 560, padding: 24 }}>
          <Alert
            showIcon
            action={<Button onClick={() => {
              this.setState({ failed: false }); this.props.onRetry?.();
            }}
            >{this.props.retryLabel}</Button>}
            description={this.props.description}
            title={this.props.title}
            type="error"
          />
        </main>
      );
    }
    return this.props.children;
  }
}

/** RealmProfileUnavailableShell 为非法或停用 Realm 提供不含任何渠道业务内容的失败关闭界面。 */
function RealmProfileUnavailableShell({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <main style={{ margin: '15vh auto', maxWidth: 560, padding: 24 }}>
      <Alert
        showIcon
        action={onRetry ? <Button onClick={onRetry}>{t('Common:retry')}</Button> : undefined}
        description={t('Common:realmProfile.unavailableDescription')}
        title={t('Common:realmProfile.unavailableTitle')}
        type="error"
      />
    </main>
  );
}

/** RealmProfileRenderer 用唯一 DynamicView 链路渲染发布、预览和 canonical fallback。 */
export function RealmProfileRenderer({ loading = false, onRetry, profile, source }: RealmProfileRendererProps) {
  const { t } = useTranslation();
  const context = useMemo(() => ({ loading, profile, retry: onRetry ?? (() => undefined), source }), [loading, onRetry, profile, source]);
  if (loading) {
    return <main aria-busy="true" aria-live="polite" style={{ margin: '48px auto', maxWidth: 1200, padding: 24 }}><Skeleton active /></main>;
  }
  if (source === 'unavailable' || !profile) return <RealmProfileUnavailableShell onRetry={onRetry} />;
  return (
    <RealmProfileContext.Provider value={context}>
      <ConfigProvider theme={createTenantWebRealmTheme(profile)}>
        <RealmProfileErrorBoundary
          description={t('Common:realmProfile.renderFailedDescription')}
          retryLabel={t('Common:retry')}
          title={t('Common:realmProfile.renderFailedTitle')}
          onRetry={onRetry}
        >
          <main>
            <React.Suspense fallback={<Skeleton active style={{ margin: '48px auto', maxWidth: 1200 }} />}>
              <DynamicView
                components={realmHomeRuntimeComponents}
                schema={profile.webProfile.homepage.schema as unknown as DynamicViewProps['schema']}
                schemaKey={profile.contentHash}
                scope={{ components: realmHomeRuntimeComponents }}
                store={profile.webProfile.homepage.store as unknown as DynamicViewProps['store']}
              />
            </React.Suspense>
          </main>
        </RealmProfileErrorBoundary>
      </ConfigProvider>
    </RealmProfileContext.Provider>
  );
}
